import { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { OperationMS, TabDescriptor } from '../types';
import { buildOperationTabs, computeDefaultTabKind } from '../domain/operationTabs';

// Hook mince : la logique (gating, onglet par defaut) vit dans domain/operationTabs.
// Ici, uniquement le cablage de l'onglet actif a l'URL (?tab=).
export const useOperationMsTabs = (operation: OperationMS | null) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const descriptors = useMemo<TabDescriptor[]>(
    () => (operation ? buildOperationTabs(operation) : []),
    [operation],
  );

  const defaultIndex = useMemo(() => {
    if (!operation) return 0;
    const kind = computeDefaultTabKind(operation);
    const i = descriptors.findIndex((d) => d.kind === kind);
    return i === -1 ? 0 : i;
  }, [descriptors, operation]);

  // ?tab= accepte un INDEX numerique OU un kind (ex. 'questions_reponses') -> un seul contrat.
  const tabParam = searchParams.get('tab');
  const activeIndex = useMemo(() => {
    if (tabParam !== null) {
      const parsed = parseInt(tabParam, 10);
      if (!Number.isNaN(parsed) && parsed >= 0 && parsed < descriptors.length) return parsed;
      const byKind = descriptors.findIndex((d) => d.kind === tabParam);
      if (byKind !== -1) return byKind;
    }
    return defaultIndex;
  }, [tabParam, defaultIndex, descriptors]);

  const setActiveIndex = useCallback(
    (index: number) => {
      const target = descriptors[index];
      if (!target || target.disabled || target.inactive) return;
      const next = new URLSearchParams(searchParams);
      next.set('tab', String(index));
      setSearchParams(next, { replace: true });
    },
    [descriptors, searchParams, setSearchParams],
  );

  return {
    descriptors,
    activeIndex,
    activeTab: descriptors[activeIndex] ?? null,
    setActiveIndex,
  };
};
